import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { AED, deliveryFeeForBox, useDeliveryFees } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ImagePlus, X, Trash2, EyeOff, Eye, ArrowLeft, Lock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const imgSrc = (s) => (!s ? "" : s.startsWith("http") || s.startsWith("data:") || s.startsWith("/") ? s : `data:image/jpeg;base64,${s}`);
const BOXES = ["Wrapped", "Small", "Medium", "Large"];
const CONDITIONS = ["New", "Like New", "Good", "Fair", "Poor"];
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

async function fileToBase64(file) {
  return new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });
}

export default function EditListing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const feeConfig = useDeliveryFees();
  const [l, setL] = useState(null);
  const [desired, setDesired] = useState("");
  const [floor, setFloor] = useState("");
  const [box, setBox] = useState("");
  const origRef = useRef({ desired: null, floor: null, box: null });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [modal, setModal] = useState(null); // { pr, boxChanged } | null
  const [sizeConfirmed, setSizeConfirmed] = useState(false);
  const [descAccurate, setDescAccurate] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => {
      setL({ ...data, specs: (data.specs || []).filter((s) => !/^\s*condition\s*:/i.test(s)) });
      setDesired(data.seller_desired_payout != null ? String(data.seller_desired_payout) : "");
      setFloor(data.seller_floor_payout != null ? String(data.seller_floor_payout) : "");
      setBox(data.preferred_box_size || "");
      origRef.current = {
        desired: data.seller_desired_payout ?? null,
        floor: data.seller_floor_payout ?? null,
        box: data.preferred_box_size || "",
        title: data.title, description: data.description, condition: data.condition,
        specs: (data.specs || []).filter((s) => !/^\s*condition\s*:/i.test(s)),
      };
    }).catch(() => { toast.error("Could not load listing"); navigate("/profile"); });
  }, [id, navigate]);

  if (!l) return <div className="py-20 text-center text-slate-400">Loading…</div>;

  const isFree = l.listing_tier === "free" || l.listing_type === "free";
  const locked = !!l.edit_locked;

  const setField = (k, v) => setL({ ...l, [k]: v });
  const setSpec = (i, v) => setL({ ...l, specs: l.specs.map((s, x) => (x === i ? v : s)) });
  const removeSpec = (i) => setL({ ...l, specs: l.specs.filter((_, x) => x !== i) });
  const addSpec = () => setL({ ...l, specs: [...(l.specs || []), ""] });

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const b64 = await Promise.all(files.map(fileToBase64));
    setL({ ...l, seller_photos: [...(l.seller_photos || []), ...b64] });
  };
  const removePhoto = (i) => setL({ ...l, seller_photos: l.seller_photos.filter((_, x) => x !== i) });

  const priceChanged = !isFree && (Number(desired) !== Number(origRef.current.desired) || Number(floor) !== Number(origRef.current.floor));
  const boxChanged = !isFree && (box || "") !== (origRef.current.box || "");
  const descChanged = (l.title !== origRef.current.title) || ((l.description || "") !== (origRef.current.description || "")) || ((l.condition || "") !== (origRef.current.condition || "")) || (JSON.stringify(l.specs || []) !== JSON.stringify(origRef.current.specs || []));

  const basePayload = () => ({
    title: l.title, description: l.description, category: l.category, subcategory: l.subcategory || "",
    condition: l.condition, brand: l.brand || "", model: l.model || "", serial_number: l.serial_number || "",
    specs: [...(l.condition ? [`Condition: ${l.condition}`] : []), ...(l.specs || []).filter((s) => s.trim())], has_original_box: !!l.has_original_box, has_receipt: !!l.has_receipt,
    accessories: l.accessories || [], listing_tier: l.listing_tier,
    seller_photos: l.seller_photos || [], ai_thumbnail: l.ai_thumbnail || "",
    seller_area: l.seller_area || "", seller_emirate: l.seller_emirate || "",
    rrp_price: Number(l.rrp_price) || 0, used_market_avg: Number(l.used_market_avg) || 0,
    // existing public prices sent only to satisfy the API model — the server derives them from the figures below
    listing_price: Number(l.listing_price) || 0, floor_price: Number(l.floor_price) || 0,
    seller_desired_payout: isFree ? null : Number(desired) || 0,
    seller_floor_payout: isFree ? null : Number(floor) || 0,
    preferred_box_size: box || undefined,
    ...(descChanged ? { desc_accurate: true, desc_accurate_at: new Date().toISOString(), seller_counterfeit_confirmed: true, seller_counterfeit_confirmed_at: new Date().toISOString() } : {}),
  });

  const doSave = async (payload) => {
    setSaving(true);
    try {
      await api.put(`/listings/${id}`, payload);
      toast.success("Changes saved");
      navigate(`/listing/${id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409) { toast.error(d || "This listing can't be edited right now"); }
      else toast.error(d || "Could not save");
      setSaving(false);
      setModal(null);
    }
  };

  const onSaveClick = async () => {
    if (locked) return;
    if (descChanged && !descAccurate) {
      toast.error("Please confirm the title, description and specs accurately describe your item"); return;
    }
    if (!isFree) {
      if ((!desired || Number(desired) <= 0) || (!floor || Number(floor) <= 0)) {
        toast.error("Please enter the price you want and your lowest acceptable amount"); return;
      }
      if (Number(floor) > Number(desired)) {
        toast.error("Your lowest acceptable amount can't be higher than the price you want"); return;
      }
    }
    // No price/packaging change → save straight away, no modal
    if (!priceChanged && !boxChanged) { doSave(basePayload()); return; }
    // Changed → recalc buyer pricing and open the approval modal
    try {
      const { data: pr } = await api.post("/listings/pricing-preview", {
        seller_desired_payout: Number(desired), seller_floor_payout: Number(floor), preferred_box_size: box,
      });
      setSizeConfirmed(false);
      setModal({ pr, boxChanged });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not calculate pricing"); }
  };

  const acceptModal = () => {
    if (modal.boxChanged && !sizeConfirmed) { toast.error("Please confirm the size requirements"); return; }
    const now = new Date().toISOString();
    doSave({
      ...basePayload(),
      seller_price_approved: true,
      seller_price_approved_at: now,
      ...(modal.boxChanged ? { seller_size_confirmed: true, seller_size_confirmed_at: now } : {}),
    });
  };

  const toggleHide = async () => {
    const next = l.status === "hidden" ? "active" : "hidden";
    try { await api.post(`/listings/${id}/status`, { status: next }); setField("status", next); toast.success(next === "hidden" ? "Listing hidden from browse" : "Listing is live again"); }
    catch { toast.error("Could not update visibility"); }
  };

  const del = async () => {
    try { await api.delete(`/listings/${id}`); toast.success("Listing deleted"); navigate("/profile"); }
    catch { toast.error("Could not delete"); }
  };

  const pr = modal?.pr;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <button onClick={() => navigate(`/listing/${id}`)} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="edit-back"><ArrowLeft className="h-4 w-4" /> Back to listing</button>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Edit Listing</h1>
        <Badge className={`rounded-full ${l.status === "hidden" ? "bg-slate-200 text-slate-700" : "bg-emerald-100 text-emerald-700"}`}>{cap(l.status)}</Badge>
      </div>

      {locked && (
        <div data-testid="edit-locked-banner" className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{l.edit_locked_reason || "This listing can't be edited right now."}</span>
        </div>
      )}

      <fieldset disabled={locked} className={locked ? "pointer-events-none opacity-60" : ""}>
        <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6">
          <div>
            <Label className="mb-1 block">Title</Label>
            <Input data-testid="edit-listing-title" value={l.title} onChange={(e) => setField("title", e.target.value)} maxLength={80} className="rounded-lg" />
          </div>
          <div>
            <Label className="mb-1 block">Description</Label>
            <Textarea data-testid="edit-listing-description" value={l.description} onChange={(e) => setField("description", e.target.value)} rows={6} className="rounded-lg" />
          </div>

          <div>
            <Label className="mb-1 block">Condition</Label>
            <Select value={l.condition || ""} onValueChange={(v) => setField("condition", v)}>
              <SelectTrigger data-testid="edit-condition" className="rounded-lg"><SelectValue placeholder="Select condition" /></SelectTrigger>
              <SelectContent>
                {CONDITIONS.map((c) => <SelectItem key={c} value={c} data-testid={`edit-condition-${c.replace(/\s+/g, "-")}`}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-1 block">Key specs</Label>
            <div className="space-y-2">
              {(l.specs || []).map((s, i) => (
                <div key={i} className="flex gap-2">
                  <Input data-testid={`edit-spec-${i}`} value={s} onChange={(e) => setSpec(i, e.target.value)} className="rounded-lg" />
                  <Button variant="outline" size="icon" onClick={() => removeSpec(i)} className="shrink-0 rounded-lg"><X className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addSpec} data-testid="edit-add-spec" className="rounded-full">+ Add spec</Button>
            </div>
          </div>

          {!isFree && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="mb-1 flex min-h-[2.75rem] items-end leading-snug">How much would you like to receive for{"\u00A0"}it?</Label>
                <div className="flex items-center rounded-lg border border-slate-200 bg-white pl-3">
                  <span className="text-sm font-semibold text-slate-500">AED</span>
                  <Input data-testid="edit-desired-payout" inputMode="numeric" value={desired} onChange={(e) => setDesired(e.target.value.replace(/[^0-9]/g, ""))} className="border-0 bg-transparent focus-visible:ring-0" />
                </div>
                <p className="mt-1 text-xs text-slate-400">This is what you'll receive when it sells.</p>
              </div>
              <div>
                <Label className="mb-1 flex min-h-[2.75rem] items-end leading-snug">What's the lowest amount you'd accept?</Label>
                <div className="flex items-center rounded-lg border border-slate-200 bg-white pl-3">
                  <span className="text-sm font-semibold text-slate-500">AED</span>
                  <Input data-testid="edit-floor-payout" inputMode="numeric" value={floor} onChange={(e) => setFloor(e.target.value.replace(/[^0-9]/g, ""))} className="border-0 bg-transparent focus-visible:ring-0" />
                </div>
                <p className="mt-1 text-xs text-slate-400">Buyers never see this. Max will never let a sale drop below it.</p>
              </div>
            </div>
          )}

          {!isFree && (
            <div>
              <Label className="mb-1 block">Packaging</Label>
              <div className="flex flex-wrap gap-2" data-testid="edit-packaging">
                {BOXES.map((b) => {
                  const fee = deliveryFeeForBox(b, feeConfig);
                  return (
                    <button key={b} type="button" data-testid={`edit-box-${b}`} onClick={() => setBox(b)}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${box === b ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-700 hover:border-primary"}`}>
                      {b}{fee != null ? ` · ${AED(fee)}` : ""}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-slate-400">Changing packaging changes the delivery fee, so you'll re-confirm the size.</p>
            </div>
          )}

          <div>
            <Label className="mb-2 block">Photos</Label>
            <div className="grid grid-cols-4 gap-3">
              {(l.seller_photos || []).map((p, i) => (
                <div key={i} className="relative aspect-square overflow-hidden rounded-xl border border-slate-200">
                  <img src={imgSrc(p)} alt="" className="h-full w-full object-cover" />
                  <button data-testid={`edit-remove-photo-${i}`} onClick={() => removePhoto(i)} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label data-testid="edit-add-photo" className="flex aspect-square cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-slate-300 text-slate-400 hover:border-primary hover:text-primary">
                <ImagePlus className="h-6 w-6" />
                <input type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} />
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            {descChanged && (
              <label className="flex w-full cursor-pointer items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs leading-snug text-slate-700" data-testid="edit-confirm-check-wrap">
                <input type="checkbox" data-testid="edit-confirm-check-checkbox" checked={descAccurate} onChange={(e) => setDescAccurate(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <span className="font-semibold text-slate-800">I confirm that:</span>
                  <ul className="mt-1.5 list-disc space-y-1 pl-4 text-slate-600">
                    <li>This item is genuine, legally owned by me, and not counterfeit, replica or stolen</li>
                    <li>I've checked the title, description and specs, and they accurately describe my item</li>
                  </ul>
                </span>
              </label>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <Button data-testid="edit-save" onClick={onSaveClick} disabled={saving || locked || (descChanged && !descAccurate)} className="rounded-full bg-primary font-semibold hover:bg-primary/90">{saving ? "Saving…" : "Save Changes"}</Button>
            <Button data-testid="edit-cancel" variant="outline" onClick={() => navigate(`/listing/${id}`)} className="rounded-full">Cancel</Button>
          </div>
        </div>
      </fieldset>

      <div className="mt-6 flex flex-wrap gap-3 rounded-2xl border border-slate-200 bg-white p-6">
        <Button data-testid="edit-hide" variant="outline" onClick={toggleHide} disabled={locked} className="rounded-full">
          {l.status === "hidden" ? <><Eye className="mr-1 h-4 w-4" /> Make live</> : <><EyeOff className="mr-1 h-4 w-4" /> Hide listing</>}
        </Button>
        <Button data-testid="edit-delete" variant="outline" onClick={() => setConfirmDelete(true)} className="rounded-full border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="mr-1 h-4 w-4" /> Delete permanently</Button>
      </div>

      {/* Pricing-approval modal — same breakdown as the Sell-with-AI pricing step */}
      <Dialog open={!!modal} onOpenChange={(o) => { if (!o) setModal(null); }}>
        <DialogContent className="rounded-2xl" data-testid="pricing-approval-modal">
          <DialogHeader>
            <DialogTitle>Review your updated pricing</DialogTitle>
            <DialogDescription>Nothing is saved until you accept these figures.</DialogDescription>
          </DialogHeader>
          {pr && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-left">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your pricing</p>
              <div className="mt-3 flex items-stretch gap-3">
                <div className="flex-1">
                  <p className="text-xs text-slate-500">Buyer pays</p>
                  <p className="font-heading text-2xl font-extrabold text-slate-900" data-testid="modal-buyer-pays">{AED(pr.listing_price)}</p>
                  <p className="mt-1 text-[11px] leading-tight text-slate-400">Your price + delivery &amp; service fee</p>
                </div>
                <div className="flex items-center text-xl font-bold text-slate-300">=</div>
                <div className="flex-1">
                  <p className="text-xs text-slate-500">You receive</p>
                  <p className="font-heading text-2xl font-extrabold text-primary" data-testid="modal-you-receive">{AED(pr.seller_desired_payout)}</p>
                  <p className="mt-1 text-[11px] leading-tight text-slate-400">Exactly what you asked for</p>
                </div>
              </div>
              <p className="mt-3 text-xs italic text-slate-400">If Max negotiates, you'll never receive less than {AED(pr.seller_floor_payout)}. Buyers never see this.</p>
              <div className="mt-3 space-y-1 border-t pt-2 text-xs text-slate-500">
                <div className="flex justify-between"><span>Your price (you receive)</span><span>{AED(pr.seller_desired_payout)}</span></div>
                <div className="flex justify-between"><span>Delivery</span><span>{AED(pr.delivery_fee)}</span></div>
                <div className="flex justify-between"><span>SellStuff service fee</span><span>{AED(pr.commission_amount)}</span></div>
                <div className="flex justify-between border-t pt-1 font-semibold text-slate-700"><span>Buyer pays</span><span data-testid="modal-breakdown-total">{AED(pr.listing_price)}</span></div>
              </div>
            </div>
          )}
          <p className="text-xs text-slate-500" data-testid="modal-offers-note">Changing your price applies to any offers still being negotiated.</p>
          {modal?.boxChanged && (
            <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="edit-collection-terms">
              <label className="flex items-start gap-2 text-xs font-semibold leading-snug text-slate-700">
                <input type="checkbox" data-testid="edit-size-confirm-checkbox" checked={sizeConfirmed} onChange={(e) => setSizeConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0" />
                <span>I agree to SellStuff's collection terms</span>
              </label>
              <ul className="mt-2 space-y-1 pl-6 text-[11px] leading-snug text-slate-500">
                <li>· My item is under 5kg and fits the packaging I selected</li>
                <li>· If it doesn't fit, the courier may offer a packaging upgrade. I can accept for AED 25, deducted from my payout, or decline, in which case a failed collection fee of AED 49 applies</li>
                <li>· If I'm unavailable or not ready at collection, a failed collection fee of AED 49 applies</li>
                <li>· If I don't confirm a collection slot within 72 hours of a sale, or I cancel, a fee of up to <span data-testid="edit-collection-terms-penalty">AED {Math.max(10, Math.round(0.03 * Number(pr?.listing_price || 0)))}</span> applies</li>
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="pricing-modal-cancel" variant="outline" onClick={() => setModal(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="pricing-modal-accept" onClick={acceptModal} disabled={saving || (modal?.boxChanged && !sizeConfirmed)} className="rounded-full bg-primary font-semibold hover:bg-primary/90">{saving ? "Saving…" : "Accept & save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="edit-delete-confirm" onClick={del} className="rounded-full bg-red-600 hover:bg-red-700">Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
